// CDN URL 변환 유틸리티

// CloudFront 도메인 설정 (환경변수 또는 하드코딩)
const CDN_DOMAIN = process.env.REACT_APP_CDN_DOMAIN || '';

// S3 URL을 CDN URL로 변환
export const convertToCdnUrl = (url) => {
  if (!url) return url;

  // 이미 CDN URL인 경우 그대로 반환
  if (CDN_DOMAIN && url.includes(CDN_DOMAIN)) {
    return url;
  }

  // S3 URL 패턴 확인
  const s3Pattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3Pattern);

  if (match && CDN_DOMAIN) {
    // S3 URL을 CDN URL로 변환
    const key = match[3];
    return `https://${CDN_DOMAIN}/${key}`;
  }

  // CDN 도메인이 없거나 S3 URL이 아닌 경우 원본 반환
  return url;
};

// 여러 이미지 URL을 일괄 변환
export const convertImagesToCdn = (images) => {
  if (!Array.isArray(images)) return images;

  return images.map(image => {
    if (typeof image === 'string') {
      return convertToCdnUrl(image);
    }

    if (image && image.image_url) {
      return {
        ...image,
        image_url: convertToCdnUrl(image.image_url)
      };
    }

    return image;
  });
};

// 메뉴 하나의 이미지 필드들을 CDN URL 로 바꾼다.
//
// image_url 뿐 아니라 image 도 바꿔야 한다 — orderPage 가 로딩하면서
// `image: menu.image_url || null` 로 파생시키고, 메뉴목록·옵션시트·장바구니가
// 그 image 를 읽어 화면에 그리기 때문이다. 하나만 바꾸면 절반만 CDN 을 탄다.
const convertMenuImageFields = (menu) => {
  if (!menu || typeof menu !== 'object') return menu;
  const out = { ...menu };
  if (out.image_url) out.image_url = convertToCdnUrl(out.image_url);
  if (out.image) out.image = convertToCdnUrl(out.image);
  return out;
};

// 메뉴 데이터의 이미지 URL을 CDN URL로 변환.
//
// 두 가지 형태를 모두 받는다.
//   1) [{ category_name, menus: [...] }, ...]        배열 형태
//   2) { '카테고리명': [menu, ...], ... }             카테고리명을 키로 갖는 객체
//
// 예전엔 배열만 처리하고 그 외에는 입력을 그대로 돌려줬는데, 실제 호출부
// (orderPage / DemoOrderPage)가 넘기는 dbMenuData 는 2번 형태라 변환이 통째로
// 건너뛰어졌다. 그래서 CloudFront 가 붙어 있는데도 손님은 S3 에서 직접 받고 있었다.
export const convertMenuImagesToCdn = (categories) => {
  if (!categories || typeof categories !== 'object') return categories;

  if (Array.isArray(categories)) {
    return categories.map(category => ({
      ...category,
      menus: Array.isArray(category?.menus) ? category.menus.map(convertMenuImageFields) : []
    }));
  }

  const out = {};
  for (const key of Object.keys(categories)) {
    const list = categories[key];
    out[key] = Array.isArray(list) ? list.map(convertMenuImageFields) : list;
  }
  return out;
};

// 이미지 로딩 최적화 (lazy loading + CDN)
//
// ※ 주의 — w/h/q/fm 파라미터는 현재 CloudFront 배포가 지원하지 않는다.
//   실측 결과 ?w=400&q=70 을 붙여도 원본과 바이트 수가 동일했다(파라미터 무시).
//   리사이즈가 필요하면 업로드 시점에 줄인다(src/utils/imageCompressor.js).
//   호출부가 없는 상태이며, "이미 최적화가 되어 있다"고 오해하지 말 것.
export const optimizeImageLoading = (imageUrl, options = {}) => {
  const cdnUrl = convertToCdnUrl(imageUrl);

  // 이미지 파라미터 추가 (CloudFront에서 처리 가능한 경우)
  const params = new URLSearchParams();

  if (options.width) params.append('w', options.width);
  if (options.height) params.append('h', options.height);
  if (options.quality) params.append('q', options.quality);
  if (options.format) params.append('fm', options.format);

  const queryString = params.toString();
  return queryString ? `${cdnUrl}?${queryString}` : cdnUrl;
};

// CDN 프리페치 (중요한 이미지 미리 로드)
export const prefetchImages = (imageUrls) => {
  if (!Array.isArray(imageUrls)) return;

  imageUrls.forEach(url => {
    const cdnUrl = convertToCdnUrl(url);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'image';
    link.href = cdnUrl;
    document.head.appendChild(link);
  });
};

export default {
  convertToCdnUrl,
  convertImagesToCdn,
  convertMenuImagesToCdn,
  optimizeImageLoading,
  prefetchImages
};